import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../AuthContext';

const API = '/api';

const MONTH_NAMES = [
  '', 'Ιανουάριος', 'Φεβρουάριος', 'Μάρτιος', 'Απρίλιος',
  'Μάιος', 'Ιούνιος', 'Ιούλιος', 'Αύγουστος',
  'Σεπτέμβριος', 'Οκτώβριος', 'Νοέμβριος', 'Δεκέμβριος'
];

export default function Dashboard() {
  const { isAdmin } = useAuth();
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedYear,  setSelectedYear]  = useState(now.getFullYear());
  const [filterMode,    setFilterMode]    = useState('month');
  const [data,    setData]    = useState(null);
  const [reversedTickets, setReversedTickets] = useState([]);
  const [reversedLoading, setReversedLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [exporting, setExporting] = useState(false);

  const fetchDashboard = useCallback(() => {
    setLoading(true);
    const params = filterMode === 'month' ? { month: selectedMonth, year: selectedYear } : {};
    axios.get(`${API}/dashboard`, { params })
      .then(r => setData(r.data))
      .catch(() => setError('Σφάλμα φόρτωσης dashboard'))
      .finally(() => setLoading(false));
  }, [selectedMonth, selectedYear, filterMode]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  useEffect(() => {
    async function fetchReversed() {
      if (!isAdmin) return;
      setReversedLoading(true);
      try {
        const params = filterMode === 'month' ? { month: selectedMonth, year: selectedYear } : {};
        const response = await axios.get(`${API}/admin/reversed-tickets`, { params });
        setReversedTickets(response.data);
      } catch {
        setError('Σφάλμα φόρτωσης πιστωτικών tickets');
      } finally {
        setReversedLoading(false);
      }
    }
    fetchReversed();
  }, [isAdmin, selectedMonth, selectedYear, filterMode]);

  async function exportEmails() {
    setExporting(true);
    try {
      const params = filterMode === 'month' ? `?month=${selectedMonth}&year=${selectedYear}` : '';
      const response = await axios.get(`${API}/admin/export-emails${params}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `mrgadget_emails_${filterMode === 'month' ? `${selectedYear}_${selectedMonth}` : 'all'}.xlsx`);
      document.body.appendChild(link); link.click(); link.remove();
    } catch { alert('Σφάλμα export'); }
    finally { setExporting(false); }
  }

  async function exportTickets() {
    setExporting(true);
    try {
      const params = filterMode === 'month' ? `?month=${selectedMonth}&year=${selectedYear}` : '';
      const response = await axios.get(`${API}/admin/export-tickets${params}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `mrgadget_tickets_${filterMode === 'month' ? `${selectedYear}_${selectedMonth}` : 'all'}.xlsx`);
      document.body.appendChild(link); link.click(); link.remove();
    } catch { alert('Σφάλμα export'); }
    finally { setExporting(false); }
  }

  if (error) return <div className="fade-in"><div className="alert alert-error">{error}</div></div>;

  const { revenue, expenses, parts_used, gross_profit, profit, parts_count, tickets_count } = data || {};
  const periodLabel = filterMode === 'all' ? 'Συνολικά (από την αρχή)' : `${MONTH_NAMES[selectedMonth]} ${selectedYear}`;
  const profitPositive = (profit||0) >= 0;

  return (
    <div className="fade-in">
      <div className="page-header">
        <div>
          <div className="page-title">Στατιστικά</div>
          <div className="page-subtitle">{periodLabel}</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {isAdmin && (
            <>
              <button className="btn-ghost btn-sm" onClick={exportEmails} disabled={exporting} style={{ fontSize: 12 }}>Export Emails</button>
              <button className="btn-ghost btn-sm" onClick={exportTickets} disabled={exporting} style={{ fontSize: 12 }}>Export Tickets</button>
            </>
          )}
          <select value={filterMode} onChange={e => setFilterMode(e.target.value)} style={{ width: 'auto', minWidth: 140 }}>
            <option value="month">Ανά Μήνα</option>
            <option value="all">Συνολικά (όλα)</option>
          </select>
          {filterMode === 'month' && (
            <>
              <select value={selectedMonth} onChange={e => setSelectedMonth(parseInt(e.target.value))} style={{ width: 'auto', minWidth: 130 }}>
                {MONTH_NAMES.slice(1).map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
              </select>
              <select value={selectedYear} onChange={e => setSelectedYear(parseInt(e.target.value))} style={{ width: 'auto', minWidth: 80 }}>
                {Array.from({ length: 5 }, (_, i) => now.getFullYear() - i).map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </>
          )}
        </div>
      </div>

      {loading ? (
        <div className="empty-state"><div className="empty-state-text">Φόρτωση...</div></div>
      ) : (
        <>
          {/* ── Grouped KPI Layout ── */}
          <div className="dashboard-kpi-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 28 }}>

            {/* GROUP 1 — ΕΣΟΔΑ */}
            <div style={{ background: 'var(--surface)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 12, padding: 18 }}>
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--green)', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--green)', display: 'inline-block' }} />
                Έσοδα
              </div>
              <GroupCard label="Συνολικά Έσοδα" value={`€${revenue.total.toFixed(2)}`} sub={`${revenue.count} ολοκληρωμένες επισκευές`} color="var(--green)" large />
              <div style={{ borderTop: '1px solid var(--border)', margin: '12px 0' }} />
              <GroupCard label="Κόστος Ανταλλακτικών" value={`€${(parts_used||0).toFixed(2)}`} sub="που χρησιμοποιήθηκαν" color="var(--yellow)" />
              <div style={{ marginTop: 10, padding: '8px 12px', background: 'rgba(59,130,246,0.08)', borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Κέρδος Επισκευών</span>
                <span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, color: 'var(--accent)', fontSize: 15 }}>€{(gross_profit||0).toFixed(2)}</span>
              </div>
            </div>

            {/* GROUP 2 — ΕΞΟΔΑ */}
            <div style={{ background: 'var(--surface)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 12, padding: 18 }}>
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--danger)', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--danger)', display: 'inline-block' }} />
                Έξοδα
              </div>
              <GroupCard label="Αγορές Αποθήκης" value={`€${expenses.total.toFixed(2)}`} sub={`${expenses.count} αγορές stock (με ΦΠΑ)`} color="var(--danger)" large />
              {filterMode === 'all' && (
                <>
                  <div style={{ borderTop: '1px solid var(--border)', margin: '12px 0' }} />
                  <GroupCard label="Ανταλλακτικά" value={parts_count} sub="καταχωρημένα είδη" color="var(--text-dim)" />
                  <GroupCard label="Tickets" value={tickets_count} sub="συνολικά" color="var(--text-dim)" />
                </>
              )}
            </div>

            {/* GROUP 3 — ΑΠΟΤΕΛΕΣΜΑ */}
            <div style={{
              background: profitPositive ? 'rgba(34,197,94,0.07)' : 'rgba(239,68,68,0.07)',
              border: `2px solid ${profitPositive ? 'var(--green)' : 'var(--danger)'}`,
              borderRadius: 12, padding: 18,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              textAlign: 'center',
            }}>
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: profitPositive ? 'var(--green)' : 'var(--danger)', marginBottom: 16 }}>
                {profitPositive ? '▲' : '▼'} Καθαρό Αποτέλεσμα
              </div>
              <div style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 900, fontSize: 38, color: profitPositive ? 'var(--green)' : 'var(--danger)', lineHeight: 1, marginBottom: 10 }}>
                €{(profit||0).toFixed(2)}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 16 }}>Έσοδα − Αγορές Αποθήκης</div>
              <div style={{ width: '100%', borderTop: '1px solid var(--border)', paddingTop: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 6 }}>
                  <span style={{ color: 'var(--text-muted)' }}>Έσοδα</span>
                  <span style={{ color: 'var(--green)', fontWeight: 600 }}>+€{revenue.total.toFixed(2)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                  <span style={{ color: 'var(--text-muted)' }}>Αποθήκη</span>
                  <span style={{ color: 'var(--danger)', fontWeight: 600 }}>−€{expenses.total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Tables */}
          <div className="top-scrollbar" style={{ display: 'flex', gap: 20, paddingBottom: 8 }}>
            <div className="card" style={{ minWidth: 560, flex: '0 0 560px' }}>
              <div className="card-title">Εισροές Αποθήκης — {periodLabel}</div>
              {expenses.recent.length === 0 ? (
                <div className="empty-state" style={{ padding: '20px 0' }}><div className="empty-state-text">Δεν υπάρχουν κινήσεις</div></div>
              ) : (
                <div className="table-wrap">
                  <table>
                    <thead><tr><th>Ανταλλακτικό</th><th>Ποσότητα</th><th>Κόστος/τεμ.</th><th>Σύνολο</th><th>Από</th><th>Ημ/νία</th></tr></thead>
                    <tbody>
                      {expenses.recent.map(m => (
                        <tr key={m.id}>
                          <td>
                            <div style={{ fontSize: 12, color: 'var(--accent)' }} className="mono">{m.part_code}</div>
                            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{m.part_name}</div>
                          </td>
                          <td style={{ fontWeight: 700 }}>+{m.quantity}</td>
                          <td><span className="mono">€{parseFloat(m.cost_per_unit).toFixed(2)}</span></td>
                          <td><span className="mono" style={{ color: 'var(--danger)', fontWeight: 700 }}>€{parseFloat(m.total_cost).toFixed(2)}</span></td>
                          <td style={{ fontSize: 11, color: 'var(--text-muted)' }}>{m.moved_by}</td>
                          <td style={{ fontSize: 11, color: 'var(--text-muted)' }}>{formatDate(m.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="card" style={{ minWidth: 480, flex: '0 0 480px' }}>
              <div className="card-title">Ολοκληρωμένες Επισκευές — {periodLabel}</div>
              {revenue.recent.length === 0 ? (
                <div className="empty-state" style={{ padding: '20px 0' }}><div className="empty-state-text">Δεν υπάρχουν επισκευές</div></div>
              ) : (
                <div className="table-wrap">
                  <table>
                    <thead><tr><th>Ticket</th><th>Πελάτης</th><th>Έσοδο</th><th>Ημ/νία</th></tr></thead>
                    <tbody>
                      {revenue.recent.map((t, i) => (
                        <tr key={i}>
                          <td><span className="mono" style={{ fontSize: 11, color: 'var(--accent)' }}>{t.ticket_number}</span></td>
                          <td style={{ fontWeight: 600 }}>{t.customer_name}</td>
                          <td><span className="mono" style={{ color: 'var(--green)', fontWeight: 700 }}>€{parseFloat(t.total_price).toFixed(2)}</span></td>
                          <td style={{ fontSize: 11, color: 'var(--text-muted)' }}>{formatDate(t.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      )}
      {isAdmin && (
        <div className="card" style={{ marginTop: 28 }}>
          <div className="card-title">Πιστωτικά Tickets</div>
          {reversedLoading ? (
            <div className="empty-state" style={{ padding: '20px 0' }}><div className="empty-state-text">Φόρτωση...</div></div>
          ) : reversedTickets.length === 0 ? (
            <div className="empty-state" style={{ padding: '20px 0' }}><div className="empty-state-text">Δεν υπάρχουν πιστωτικά tickets</div></div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>Ticket</th><th>Πελάτης</th><th>Σύνολο</th><th>Ημ/νία</th></tr></thead>
                <tbody>
                  {reversedTickets.map((ticket) => (
                    <tr key={ticket.id}>
                      <td><span className="mono" style={{ fontSize: 11, color: 'var(--accent)' }}>{ticket.ticket_number}</span></td>
                      <td style={{ fontWeight: 600 }}>{ticket.customer_name}</td>
                      <td><span className="mono" style={{ color: 'var(--danger)', fontWeight: 700 }}>€{parseFloat(ticket.total_price).toFixed(2)}</span></td>
                      <td style={{ fontSize: 11, color: 'var(--text-muted)' }}>{formatDate(ticket.updated_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function GroupCard({ label, value, sub, color, large }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 3 }}>{label}</div>
      <div style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 800, fontSize: large ? 28 : 20, color, lineHeight: 1.1 }}>{value}</div>
      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>{sub}</div>
    </div>
  );
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}